import { ReactNode, useEffect, useMemo, useState } from 'react';
import { difference } from 'lodash';
import { Target } from '@retro-ui/api-contract';

import { apiClient } from '@f/api-client';

import { SmilesError, TargetValidationContext } from './TargetValidationContext';

interface TargetValidationProviderProps {
  children: ReactNode;
}

const MAX_TARGET_COUNT = 100000;
const MAX_SMILES_LENGTH = 500;

export function TargetValidationProvider(props: TargetValidationProviderProps) {
  const { children } = props;

  const [validationMap, setValidationMap] = useState<Record<string, string | null>>({});
  const [isProcessing, setProcessing] = useState<boolean>();

  const validateTargets = async (targets: Target[] | null) => {
    if (!targets) {
      return [];
    }

    setProcessing(true);

    if (targets.length > MAX_TARGET_COUNT) {
      setProcessing(false);
      throw new Error(`Too many targets, maximum is ${MAX_TARGET_COUNT}`);
    }

    let targetsToValidate = targets.filter(target => {
      if (validationMap[target.smiles] === undefined) {
        return true;
      }

      return false;
    }, []);

    let draftValidationMap = {
      ...validationMap,
      ...Object.fromEntries(targetsToValidate.map(target => [target.smiles, null])),
    };

    const tooLongTargets = targetsToValidate.filter(target => target.smiles.length > MAX_SMILES_LENGTH);
    if (tooLongTargets.length > 0) {
      draftValidationMap = {
        ...draftValidationMap,
        ...Object.fromEntries(
          tooLongTargets.map(target => [target.smiles, `SMILES is too long, maximum length is ${MAX_SMILES_LENGTH}`])
        ),
      };
    }

    targetsToValidate = difference(targetsToValidate, tooLongTargets).filter(target => target.smiles.length > 0);

    const errors = await apiClient.search.validateTargets({
      body: targetsToValidate.map(target => target.smiles),
    });
    if (errors.status !== 200) {
      setProcessing(false);
      throw new Error('Failed to validate targets.');
    }
    const errorCount = Object.keys(errors.body).length;
    const allSmilesValid = errorCount === 0;

    if (!allSmilesValid) {
      draftValidationMap = {
        ...draftValidationMap,
        ...Object.entries(errors.body).reduce((acc, [smiles, error]) => {
          const target = targetsToValidate.find(target => target.smiles === smiles);

          if (target) {
            acc[smiles] = error;
          }

          return acc;
        }, {}),
      };
    }

    setValidationMap(draftValidationMap);
    setProcessing(false);

    return (targets ?? [])
      .map((target, index) => ({ index, smiles: target.smiles }))
      .filter(error => typeof draftValidationMap[error.smiles] === 'string')
      .map<SmilesError>(error => ({ ...error, message: draftValidationMap[error.smiles]! }));
  };

  return <TargetValidationContext.Provider value={{ validateTargets }}>{children}</TargetValidationContext.Provider>;
}
